/**
 * Data-fetching hooks.
 *
 * Small on purpose. React Query would be the usual answer, but it is ~13 KB
 * gzipped and this app needs three behaviours: fetch, refetch, and serve cache
 * while offline. That is fifty lines.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api, getCached } from './api';
import { useLiveRevision } from './live';

export interface QueryState<T> {
  data: T | null;
  loading: boolean;
  error: ApiError | null;
  /** True when the data came from cache and may be out of date. */
  stale: boolean;
  refetch: () => Promise<void>;
}

/**
 * GET with an offline-tolerant cache.
 *
 * Pass a `cacheKey` for screens that must open with something while offline
 * (catalogue, beat plan, load sheet). Omit it for anything where stale data
 * would mislead — stock counts, cash positions.
 */
export function useQuery<T>(
  path: string | null,
  options?: { cacheKey?: string; ttlSeconds?: number; deps?: unknown[]; refreshOn?: string[] },
): QueryState<T> {
  // The live layer (lib/live). When any watched domain changes, this number
  // bumps and the effect below refetches — a quiet, ~10s refresh. 0 and inert
  // for any screen that does not pass `refreshOn`.
  const liveRevision = useLiveRevision(options?.refreshOn);
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(!!path);
  const [error, setError] = useState<ApiError | null>(null);
  const [stale, setStale] = useState(false);

  // Guards against a slow response from a previous path overwriting a newer one.
  const requestId = useRef(0);

  const run = useCallback(async () => {
    if (!path) { setLoading(false); return; }
    const id = ++requestId.current;
    setLoading(true);
    setError(null);

    try {
      if (options?.cacheKey) {
        const result = await getCached<T>(path, options.cacheKey, options.ttlSeconds ?? 3600);
        if (id !== requestId.current) return;
        setData(result.data);
        setStale(result.stale);
      } else {
        const result = await api.get<T>(path);
        if (id !== requestId.current) return;
        setData(result);
        setStale(false);
      }
    } catch (err) {
      if (id !== requestId.current) return;
      setError(err instanceof ApiError ? err : new ApiError('UNKNOWN', 'Request failed', 0));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, options?.cacheKey, options?.ttlSeconds]);

  useEffect(() => {
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, liveRevision, ...(options?.deps ?? [])]);

  return { data, loading, error, stale, refetch: run };
}

/**
 * Mutation helper.
 *
 * Exposes `busy` and `fieldErrors` because almost every form in this app needs
 * both: a disabled submit button and per-field messages from the API's
 * validation response.
 */
export function useMutation<TBody, TResult>(
  fn: (body: TBody) => Promise<TResult>,
): {
  run: (body: TBody) => Promise<TResult | null>;
  busy: boolean;
  error: ApiError | null;
  fieldErrors: Record<string, string>;
  reset: () => void;
} {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const run = useCallback(async (body: TBody) => {
    setBusy(true);
    setError(null);
    setFieldErrors({});
    try {
      return await fn(body);
    } catch (err) {
      const apiError = err instanceof ApiError ? err : new ApiError('UNKNOWN', 'Request failed', 0);
      setError(apiError);
      if (apiError.fields) setFieldErrors(apiError.fields);
      return null;
    } finally {
      setBusy(false);
    }
  }, [fn]);

  const reset = useCallback(() => {
    setError(null);
    setFieldErrors({});
  }, []);

  return { run, busy, error, fieldErrors, reset };
}

/**
 * Refetch when the tab becomes visible again.
 *
 * Field staff leave the app open all day and come back to it between shops.
 * Without this they would be looking at figures from three hours ago.
 */
export function useRefreshOnFocus(refetch: () => void | Promise<void>, enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onVisible);
    };
  }, [refetch, enabled]);
}

/**
 * Refetch on a timer while the screen is actually on display.
 *
 * `useRefreshOnFocus` fires only when the tab becomes visible or the network
 * returns, which is right for nearly everything: a screen you are looking at
 * shows what was true when you opened it, and you refresh it by leaving and
 * coming back.
 *
 * It is wrong for a screen that something ELSE changes while the user watches
 * it. A driver on his route screen gets a stop added by the office; he is
 * holding the phone, the screen never loses focus, and the new drop does not
 * appear. "Background the app and come back" is not something he knows to do,
 * and he drives past the shop.
 *
 * ONLY WHILE VISIBLE, and the interval is cleared when it is not. A poll that
 * keeps running in a backgrounded tab is a battery drain and a stream of
 * requests on a phone data plan — and on a delivery round both of those are
 * the driver's, not ours. The visible check also means the tab-return refresh
 * still does its job, so this is an addition to that behaviour rather than a
 * replacement for it.
 */
export function usePoll(
  refetch: () => void | Promise<void>,
  everyMs: number,
  enabled = true,
): void {
  useEffect(() => {
    if (!enabled) return;
    let timer: number | undefined;

    const stop = () => {
      if (timer !== undefined) { window.clearInterval(timer); timer = undefined; }
    };
    const start = () => {
      if (timer === undefined && document.visibilityState === 'visible') {
        timer = window.setInterval(() => { void refetch(); }, everyMs);
      }
    };
    const onVisibility = () => (document.visibilityState === 'visible' ? start() : stop());

    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refetch, everyMs, enabled]);
}

/** Debounce, for search boxes. */
export function useDebounced<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/** Tracks connectivity for screens that change behaviour offline. */
export function useOnline(): boolean {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}
